import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useField } from 'formik';

import { FileProgress, hooks } from '@febos-io/design';

import useUploadFilesToS3Callback from '../hooks/useUploadFilesToS3Callback';

export function FormikInputFile(
  {
    id: defaultId,
    label,
    name,
    placeholder,
    classInput,
    multiple = false,
    className,
    fileFolder,
    accept
  }
) {
  const [, { value }, helper] = useField(name);
  const [files, setFiles] = useState(value || {});

  const updateFile = useCallback(async (file) => {
    setFiles((prevState) => ({
      ...prevState,
      [file.key]: {
        ...file,
        ...prevState[file.key],
        meta: {
          ...prevState[file.key]?.meta,
          ...file.meta
        }
      }
    }));
  }, []);

  const {
    callback: uploadFiles,
    removeFile,
    loading: isLoadingFiles
  } = useUploadFilesToS3Callback(files, updateFile);

  const onChangeFiles = useCallback(async ({ target: { files: selectedFiles } }) => {
    // pass selected files to object list
    const pendingFiles = Array.from(selectedFiles).map(file => {
      const date = new Date();
      const type = file.type || 'application/octet-stream';
      const key = `${type}/${date.getTime()}.${file.name}`.replaceAll(' ', '_').replaceAll('Ñ', 'N').replaceAll('ñ', 'n').replace(/[^A-Za-z0-9-_.!~*'()]/g, '_');
      return {
        date,
        name: file.name,
        size: file.size,
        type,
        file,
        key,
        fileKey: `${fileFolder}/${key}`,
        meta: {
          progress: 0
        }
      };
    });
    const newFiles = pendingFiles.reduce((acc, file) => {
      acc[file.key] = file;
      return acc;
    }, files);
    setFiles(newFiles);
    helper.setValue(newFiles);
    await uploadFiles({ files: pendingFiles });
    helper.setValue({
      ...value,
      ...files
    });
  }, [files, helper, uploadFiles, value, fileFolder]);

  const onRemoveFile = useCallback(async (file) => {
    removeFile(file);
    const newFiles = Object.values(files)
      .filter(item => item.key !== file.key)
      .reduce((acc, item) => {
        acc[item.key] = item;
        return acc;
      }, {});
    setFiles(newFiles);
    helper.setValue(newFiles);
  }, [files, helper, removeFile]);

  const {
    id,
    ariaDescribedby
  } = hooks.useInput(defaultId, label);

  return (
    <div className={classNames(className)}>
      {label && (
        <label
          htmlFor={id}
          className="form-label"
        >
          {label}
        </label>
      )}

      <input
        id={id}
        placeholder={placeholder}
        {...(ariaDescribedby) && {
          'aria-describedby': ariaDescribedby
        }}
        type="file"
        className={classNames(
          'form-control',
          classInput
        )}
        multiple={multiple}
        onChange={onChangeFiles}
        disabled={isLoadingFiles}
        accept={accept}
      />
      {Object.keys(files).map((key) => (
        <FileProgress
          key={`file-${key}`}
          file={files[key]}
          onRemove={onRemoveFile}
        />
      ))}
    </div>
  );
}

FormikInputFile.propTypes = {
  id: PropTypes.string,
  label: PropTypes.string,
  name: PropTypes.string,
  placeholder: PropTypes.string,
  classInput: PropTypes.string,
  multiple: PropTypes.bool,
  className: PropTypes.string,
  fileFolder: PropTypes.string.isRequired,
  accept: PropTypes.string
};

export default FormikInputFile;
